import React, { useEffect } from 'react';
import { useRouter } from 'next/router';
import { ModalInstance } from '@src/lib/CustomModal/ModalContainer';
import { UserSession } from '@src/lib';
import useLogin from '@src/hooks/useLogin';

const key = 'ChatPromoModal';

/**
 * 채팅 안내(1,000냥 지급) 프로모 모달 — hearheart NiangCoinModal 디자인을 그대로 이식.
 * ⚠️ 지금은 "모양만" 구현. 결제그룹 판별 / 지급 API / 채팅 redirect 등 로직은 없음.
 */
export const openChatPromoModal = () => {
  ModalInstance.getInstance().push({
    key,
    shouldCloseOnOverlayClick: true,
    onOverlayClick: () => closeChatPromoModal(),
    component: <ChatPromoModal />,
  });
};

export const closeChatPromoModal = () => {
  ModalInstance.getInstance().delete(key);
};

const ChatPromoModal = () => {
  const router = useRouter();
  const { showJoinAndLoginModal } = useLogin();

  const dismiss = () => closeChatPromoModal();

  // 채팅 사이트 이동 — 채팅 메뉴(GnbMenu.onChatClick)와 동일한 3시나리오 로직.
  // 인증 게이팅은 /chat SSR에 중앙화됨 (auth=0 → nsfw 인증, auth=1 → 채팅).
  const goChat = () => {
    closeChatPromoModal();
    const { token } = UserSession.getUserInfo();
    if (token === '') {
      // ① 비로그인 → 로그인/회원가입 후 /chat 으로 (거기서 인증 게이트 재실행)
      showJoinAndLoginModal('/chat');
    } else {
      // ②③ 로그인 → /chat SSR 게이트: 비성인(auth=0)은 nsfw 인증, 성인(auth=1)은 채팅
      router.push('/chat');
    }
  };

  // ESC 닫기 (원본 @keydown.escape.window="$wire.dismiss()")
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dismiss();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <>
      {/* 원본 <style> 그대로 (keyframes) */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
            @keyframes _niangBlink { 0%, 60%, 100% { opacity: 0.25; transform: translateY(0); } 30% { opacity: 1; transform: translateY(-3px); } }
            @keyframes _niangPop { to { opacity: 1; transform: translateY(0); } }
            @keyframes _niangCaret { 50% { opacity: 0; } }
            .niang-bubble-anim { opacity: 0; transform: translateY(8px); animation: _niangPop .4s ease forwards; }
            /* 모바일 영상 높이 — Tailwind arbitrary(h-[..]) 는 JIT 미생성 시 붕괴하므로 여기서 직접 지정. 이 숫자만 바꾸면 됨. */
            .niang-video-box { height: 300px; }
            @media (min-width: 768px) { .niang-video-box { height: auto; } }
          `,
        }}
      />

      {/* 모달 카드 — 모바일: max-w 작게 + max-h 제한 + 내용 스크롤. PC: 760px wide row. */}
      <div className="relative z-[1] flex w-[calc(100%-24px)] max-w-[320px] md:max-w-[760px] max-h-[90vh] flex-col md:flex-row rounded-2xl md:rounded-3xl bg-[#262329] overflow-hidden shadow-2xl">
        {/* 좌: 캐릭터 영상 (PC 절반 / 모바일 상단).
            모바일 높이는 위 <style> 의 .niang-video-box 에서 지정 — 숫자만 바꾸면 됨(JIT 영향 없음).
            영상은 object-cover 로 박스를 채우고 object-top 으로 얼굴 위주.
            PC: .niang-video-box 가 height:auto 로 풀리고 md:self-stretch 로 우측 내용 높이에 맞춰 채움(원본과 동일). */}
        <div className="niang-video-box relative w-full md:w-1/2 md:self-stretch bg-black overflow-hidden shrink-0">
          <video
            src="https://showcase.chat.global.toptoon.com/character/1/video-thumbnail/4ca12488-9290-47e3-a3a3-59ae636e06d8.mp4"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            className="absolute inset-0 w-full h-full object-cover object-top md:object-center"
          />
        </div>

        {/* 우: 모달 내용 (PC 절반 / 모바일 하단). */}
        <div className="w-full md:w-1/2 flex flex-col overflow-y-auto">
          {/* 헤더: eyebrow + 제목 + X */}
          <div className="flex items-start justify-between px-4 pt-4 md:px-5 md:pt-5 pb-1">
            <div>
              <div className="hidden md:block text-[13px] font-extrabold tracking-[0.08em] text-[#ff4f90] mb-2.5">
                Toptoon Chat
              </div>
              <h2 className="text-[14px] md:text-[20px] font-extrabold leading-[1.32] text-[#f5f0f3]">
                {/* 임시 문구 (가격 확정 전). 원문: "You have received {아래 주석의 지급량} from Toptoon Chat." */}
                You&apos;ve got a new message{' '}
                {/* 가격(Mew 지급량) 부분 - 임시 주석 (지우지 말 것)
                <span className="text-[#ff2777] whitespace-nowrap">
                  <img
                    src="/images/chatPromo/mew_32x32.png"
                    alt=""
                    className="inline-block w-[18px] h-[18px] mr-0.5 align-[-3px]"
                  />
                  2,000 Mews
                </span>
                */}
                <br className="hidden md:inline" /> from Toptoon Chat.
              </h2>
            </div>
            <button
              type="button"
              onClick={dismiss}
              className="shrink-0 w-[30px] h-[30px] md:w-[34px] md:h-[34px] rounded-full bg-white/[0.08] text-[#bdb4ba] grid place-items-center ml-2 md:ml-3 hover:bg-white/[0.15] transition"
              aria-label="닫기"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* 채팅 영역 */}
          <div className="px-4 md:px-5 pt-2 md:pt-3 pb-2 flex flex-col gap-2 md:gap-2.5 min-h-[140px] md:min-h-[180px]">
            <div
              className="niang-bubble-anim max-w-[78%] self-start bg-[#3a363c] text-[#f5f0f3] px-3 md:px-4 py-2.5 md:py-[13px] rounded-2xl rounded-bl-md text-[13px] md:text-[14.5px] leading-[1.5] font-medium"
              style={{ animationDelay: '0.1s' }}
            >
              Shhh... You don’t want us<br className="hidden md:inline" /> to get
              caught, do you?
            </div>
            <div
              className="niang-bubble-anim max-w-[78%] self-end bg-[#ff2777] text-white px-3 md:px-4 py-2.5 md:py-[13px] rounded-2xl rounded-br-md text-[13px] md:text-[14.5px] leading-[1.5] font-medium"
              style={{ animationDelay: '0.5s' }}
            >
              Haa... You’re driving me nuts.
            </div>
            <div
              className="niang-bubble-anim self-start bg-[#3a363c] px-3 md:px-[18px] py-3 md:py-[15px] rounded-2xl rounded-bl-md inline-flex gap-[5px] items-center w-fit"
              style={{ animationDelay: '0.9s' }}
            >
              <span
                className="w-[7px] h-[7px] rounded-full bg-[#8f868c]"
                style={{ animation: '_niangBlink 1.4s infinite both' }}
              />
              <span
                className="w-[7px] h-[7px] rounded-full bg-[#8f868c]"
                style={{ animation: '_niangBlink 1.4s infinite both 0.2s' }}
              />
              <span
                className="w-[7px] h-[7px] rounded-full bg-[#8f868c]"
                style={{ animation: '_niangBlink 1.4s infinite both 0.4s' }}
              />
            </div>
          </div>

          {/* 보너스 뱃지 — 모바일 미노출. 가격(Mew 지급량) 부분 - 임시 주석 (지우지 말 것)
          <div className="hidden md:inline-flex mx-5 mt-3 self-start items-center gap-2 bg-[#2e2a1e] text-[#ffce5c] text-[13.5px] font-bold px-4 py-[11px] rounded-full w-fit">
            <img
              src="/images/chatPromo/mew_32x32.png"
              alt=""
              className="w-[18px] h-[18px]"
            />
            You have received 2,000 Mews.
          </div>
          */}

          {/* CTA: 입력창 위장형 */}
          <div className="px-4 md:px-5 py-4 md:py-5 mt-auto">
            <div
              role="button"
              tabIndex={0}
              onClick={goChat}
              className="w-full flex items-center gap-2 md:gap-2.5 cursor-pointer bg-[#3a363c] border-[1.5px] border-transparent rounded-full pl-4 md:pl-5 pr-1.5 md:pr-2 py-1.5 md:py-2 transition-colors hover:border-[#ff2777] hover:bg-[#1c1a1d] active:scale-[0.99]"
            >
              <span className="flex-1 text-left text-[12px] md:text-[15px] font-medium text-[#8f868c] truncate overflow-hidden whitespace-nowrap">
                Try starting a hotter conversation.
                <span
                  className="inline-block w-[1.5px] h-3 md:h-4 bg-[#ff2777] ml-0.5 align-[-3px]"
                  style={{ animation: '_niangCaret 1.1s steps(1) infinite' }}
                />
              </span>
              <span className="shrink-0 w-[36px] h-[36px] md:w-[42px] md:h-[42px] rounded-full bg-[#ff2777] text-white grid place-items-center">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 16 16"
                  fill="currentColor"
                  className="ml-0.5"
                  aria-hidden="true"
                >
                  <path d="M4 2.5v11a.5.5 0 0 0 .76.43l9-5.5a.5.5 0 0 0 0-.86l-9-5.5A.5.5 0 0 0 4 2.5z" />
                </svg>
              </span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ChatPromoModal;
